import { MAX_ASSET_TAGS, normalizeAssetTags } from './tags.js';

// THE TAG CHIP INPUT'S RULES (Run C, 2026-10-12). PURE.
//
// ⚠️⚠️ A CHIP IS A PREVIEW OF THE STORED VALUE. Every chip is produced by `normalizeAssetTags` — the same function the PATCH
// route stores through, and the one the tag filter normalises with — never by a second trim/lowercase written here. A chip
// reading "Summer" over a stored "summer" is the display-versus-stored divergence this whole module exists to avoid. The
// functions below only decide WHICH pieces of typed text to offer to `normalizeAssetTags`, one at a time, and what to say
// when it refuses.
// ⚠️ A REFUSAL IS SHOWN, NEVER A SILENT DROP: the 31st tag, or one over the length limit, is not added and comes back with the
// sentence and with the text it refused still in the box, so nothing typed disappears. (normalizeAssetTags refuses rather than
// truncates for the same reason: absence read as a value.)

export type CommitResult = {
  /** The new chip list (always valid: it came out of normalizeAssetTags). */
  tags: string[];
  /** Typed text that was NOT committed (a refused piece and everything after it); '' when everything was taken. */
  rest: string;
  /** The reason for the refusal, or null. */
  error: string | null;
};

/**
 * Commit typed or pasted text into `current`. The text is split on commas (so a pasted "a, b, c" becomes three chips, as the
 * old comma-separated field did); each piece is offered in turn. Empty pieces and duplicates are not errors — they are simply
 * not new chips, exactly as normalizeAssetTags treats them.
 */
export function commitTagText(current: readonly string[], text: string): CommitResult {
  const pieces = text.split(',');
  let tags = [...current];
  for (let i = 0; i < pieces.length; i++) {
    const attempt = normalizeAssetTags([...tags, pieces[i]!]);
    if (!attempt.ok) return { tags, rest: pieces.slice(i).join(','), error: attempt.reason };
    tags = attempt.tags;
  }
  return { tags, rest: '', error: null };
}

export function removeTagAt(tags: readonly string[], index: number): string[] {
  return tags.filter((_, i) => i !== index);
}

/** The line under the chips, so the limit is visible BEFORE it is hit: "12 of 30 tags". */
export function tagCountLine(count: number): string {
  return `${count} of ${MAX_ASSET_TAGS} tags`;
}
